import React from 'react';
import { FormattedMessage } from 'react-intl';
import { IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  ActionLink,
  FilterableTable,
  type FilterableTableColumnType,
  type FilterableTableRowMapperFunc,
} from '../../../../components';
import { Checkbox } from 'app/components';
import { TranslationKeys } from '../../../../translations';
import formatter from '../../../../utils/formatter';
import type { PensioenbijMutation } from '../../../../hooks/api/tools';
import { getPensioenbijAutoValueTranslationId } from './pensioenbijAutoValueTranslation';

export enum PensioenbijTableColumn {
  Checkbox = 'checkbox',
  SyncStatus = 'syncStatus',
  Employee = 'employeeName',
  Employer = 'employerName',
  StartDate = 'startDate',
  MutationType = 'mutationType',
  Actions = 'actions',
}

export const DASHBOARD_PENSIOENBIJ_COLUMNS: PensioenbijTableColumn[] = [
  PensioenbijTableColumn.Checkbox,
  PensioenbijTableColumn.MutationType,
  PensioenbijTableColumn.Employee,
  PensioenbijTableColumn.Employer,
  PensioenbijTableColumn.SyncStatus,
  PensioenbijTableColumn.StartDate,
];

export const CONNECTION_PENSIOENBIJ_COLUMNS: PensioenbijTableColumn[] = [
  PensioenbijTableColumn.Checkbox,
  PensioenbijTableColumn.MutationType,
  PensioenbijTableColumn.Employee,
  PensioenbijTableColumn.SyncStatus,
  PensioenbijTableColumn.StartDate,
  PensioenbijTableColumn.Actions,
];

export const EMPLOYEE_PENSIOENBIJ_COLUMNS: PensioenbijTableColumn[] = [
  PensioenbijTableColumn.Checkbox,
  PensioenbijTableColumn.MutationType,
  PensioenbijTableColumn.SyncStatus,
  PensioenbijTableColumn.StartDate,
  PensioenbijTableColumn.Actions,
];

type PensioenbijMutationsTableProps = {
  columns: PensioenbijTableColumn[];
  rows: PensioenbijMutation[];
  selectedRowsIds: string[];
  loading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    totalCount?: number;
  };
  onToggleRow: (externalId: string) => void;
  onSelectAllChange: () => void;
  onOpenMutationDetail: (mutation: PensioenbijMutation) => void;
  onPaginationChange: ({ rowSize, page }: { rowSize?: number; page?: number }) => void;
  isMutationDeletable: (mutation: PensioenbijMutation) => boolean;
};

const getMutationTypeTranslationKey = (value: string | null) => {
  if (!value) return TranslationKeys.global_selectUnknown;

  return TranslationKeys[`events_type_${value}` as keyof typeof TranslationKeys];
};

export const PensioenbijMutationsTable = ({
  columns,
  rows,
  selectedRowsIds,
  loading,
  pagination,
  onToggleRow,
  onSelectAllChange,
  onOpenMutationDetail,
  onPaginationChange,
  isMutationDeletable,
}: PensioenbijMutationsTableProps) => {
  const tableHeaders = React.useMemo<Array<FilterableTableColumnType>>(
    () =>
      columns.map(column => {
        switch (column) {
          case PensioenbijTableColumn.Checkbox:
            return { name: PensioenbijTableColumn.Checkbox, isSelectAllColumn: true };
          case PensioenbijTableColumn.SyncStatus:
            return {
              name: PensioenbijTableColumn.SyncStatus,
              title: <FormattedMessage id={TranslationKeys.events_content_header_state} />,
            };
          case PensioenbijTableColumn.Employer:
            return {
              name: PensioenbijTableColumn.Employer,
              title: <FormattedMessage id={TranslationKeys.global_employer} />,
              textAlign: 'center',
            };
          case PensioenbijTableColumn.Employee:
            return {
              name: PensioenbijTableColumn.Employee,
              title: <FormattedMessage id={TranslationKeys.events_content_header_employee} />,
              textAlign: 'center',
            };
          case PensioenbijTableColumn.StartDate:
            return {
              name: PensioenbijTableColumn.StartDate,
              title: <FormattedMessage id={TranslationKeys.events_content_header_startDate} />,
              textAlign: 'center',
            };
          case PensioenbijTableColumn.MutationType:
            return {
              name: PensioenbijTableColumn.MutationType,
              title: <FormattedMessage id={TranslationKeys.events_content_header_type} />,
              textAlign: 'center',
            };
          case PensioenbijTableColumn.Actions:
            return { name: PensioenbijTableColumn.Actions, title: '', textAlign: 'center' };
          default:
            return { name: column };
        }
      }),
    [columns],
  );

  const tableRowMapper = React.useCallback<FilterableTableRowMapperFunc<PensioenbijMutation>>(
    row => {
      const data: Record<string, React.ReactNode> = {};

      columns.forEach(column => {
        switch (column) {
          case PensioenbijTableColumn.Checkbox:
            data[PensioenbijTableColumn.Checkbox] = (
              <Checkbox
                onChange={() => onToggleRow(row.externalId)}
                name={'select-' + row.externalId}
                checked={selectedRowsIds.includes(row.externalId)}
                disabled={!isMutationDeletable(row)}
              />
            );
            break;
          case PensioenbijTableColumn.SyncStatus: {
            const syncStatusTranslationId = getPensioenbijAutoValueTranslationId('syncStatus', row.syncStatus);
            data[PensioenbijTableColumn.SyncStatus] = (
              <FormattedMessage id={syncStatusTranslationId ?? TranslationKeys.global_selectUnknown} />
            );
            break;
          }
          case PensioenbijTableColumn.Employer:
            data[PensioenbijTableColumn.Employer] = row.employerId ? (
              <ActionLink to={`/employers/${row.employerId}`}>{row.employerName}</ActionLink>
            ) : (
              <span>{row.employerName}</span>
            );
            break;
          case PensioenbijTableColumn.Employee:
            data[PensioenbijTableColumn.Employee] = row.employerId ? (
              <ActionLink to={`/employers/${row.employerId}/employees/${row.employeeRefID}`}>
                {row.employeeFirstName + (row.employeeLastName ? ' ' + row.employeeLastName : '')}
              </ActionLink>
            ) : (
              <span>{row.employeeFirstName}</span>
            );
            break;
          case PensioenbijTableColumn.StartDate:
            data[PensioenbijTableColumn.StartDate] = formatter.date(row.createdAt);
            break;
          case PensioenbijTableColumn.MutationType:
            data[PensioenbijTableColumn.MutationType] = (
              <FormattedMessage
                id={getMutationTypeTranslationKey(row.mutationType) ?? TranslationKeys.global_selectUnknown}
              />
            );
            break;
          case PensioenbijTableColumn.Actions:
            data[PensioenbijTableColumn.Actions] = (
              <Tooltip title={<FormattedMessage id={TranslationKeys.pensioenbij_mutation_detail_title} />}>
                <IconButton size="small" onClick={() => onOpenMutationDetail(row)}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
            break;
          default:
            break;
        }
      });

      return { data };
    },
    [columns, onToggleRow, selectedRowsIds, isMutationDeletable, onOpenMutationDetail],
  );

  return (
    <FilterableTable
      columns={tableHeaders}
      rows={rows}
      rowMapper={tableRowMapper}
      selectedRowsIds={selectedRowsIds}
      onSelectAllChange={onSelectAllChange}
      loading={loading}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      minFillSize={rows.length}
      hover
    />
  );
};
